import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  ProgressBar,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileSignature,
  Headphones,
  RefreshCw,
  ShieldCheck,
  Users,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CallAPIGetPromise } from "Common/commaon";
import AdminBillingDashboard from "./AdminBillingDashboard";
import "./admin-dashboard.scss";

type NumberMap = Record<string, number | string | null | undefined>;

type RecentProject = {
  id: number;
  name?: string;
  status?: string;
  total_cost?: number | string;
  created_at?: string;
  city?: string;
  state?: string;
  homeowner_name?: string;
  contractor_name?: string;
  contractor_company?: string;
  total_milestones?: number;
  completed_milestones?: number;
  contract_status?: string;
};

type RecentLead = {
  id: number;
  project_type?: string;
  status?: string;
  budget?: number | string;
  city?: string;
  state?: string;
  homeowner_name?: string;
  created_at?: string;
};

type DashboardData = {
  users: NumberMap;
  projects: NumberMap;
  contracts: NumberMap;
  leads: NumberMap;
  milestones: NumberMap;
  support: NumberMap;
  estimates: NumberMap;
  recentProjects: RecentProject[];
  recentLeads: RecentLead[];
  projectTrend: Array<{ month: string; month_key: string; total: number | string }>;
  generatedAt?: string;
};

const EMPTY_DATA: DashboardData = {
  users: {},
  projects: {},
  contracts: {},
  leads: {},
  milestones: {},
  support: {},
  estimates: {},
  recentProjects: [],
  recentLeads: [],
  projectTrend: [],
};

const numberValue = (value: unknown) => Number(value || 0);

function formatCurrency(value: unknown) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(numberValue(value));
}

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function statusVariant(status?: string) {
  const normalized = String(status || "").toUpperCase();
  if (["LIVE", "SIGNED", "COMPLETED", "ACCEPTED"].includes(normalized)) return "success";
  if (["DISPUTE", "FAILED", "EXPIRED"].includes(normalized)) return "danger";
  if (["OPEN", "AWAITING_HOMEOWNER", "AWAITING_CONTRACTOR", "PENDING"].includes(normalized)) return "warning";
  return "secondary";
}

function humanize(value?: string) {
  if (!value) return "Not started";
  return value.toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function MetricCard({
  label,
  value,
  note,
  icon: Icon,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  note: string;
  icon: React.ElementType;
  tone: string;
}) {
  return (
    <Card className="admin-kpi-card h-100">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div className="text-muted small fw-medium mb-2">{label}</div>
            <div className="admin-kpi-value">{value}</div>
            <div className="text-muted small mt-2">{note}</div>
          </div>
          <div className={`admin-kpi-icon ${tone}`}>
            <Icon size={22} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState<DashboardData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const response: any = await CallAPIGetPromise("/admin/dashboard-summary");
      setData(response?.data || EMPTY_DATA);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "The project overview could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const attentionItems = useMemo(
    () => [
      {
        label: "Contracts waiting for signature",
        value:
          numberValue(data.contracts.awaiting_homeowner) +
          numberValue(data.contracts.awaiting_contractor),
        detail: `${numberValue(data.contracts.awaiting_homeowner)} homeowner · ${numberValue(data.contracts.awaiting_contractor)} contractor`,
        icon: FileSignature,
        route: "/get-contracts",
        tone: "warning",
      },
      {
        label: "Projects still in draft",
        value: numberValue(data.projects.draft),
        detail: "Review setup, assignment, and contract readiness",
        icon: BriefcaseBusiness,
        route: "/get-contracts",
        tone: "primary",
      },
      {
        label: "Open support tickets",
        value: numberValue(data.support.open),
        detail: "Customer and contractor requests needing follow-up",
        icon: Headphones,
        route: "/apps-tickets-list",
        tone: "info",
      },
      {
        label: "Lead disputes",
        value: numberValue(data.leads.dispute),
        detail: "Escalated projects that may need intervention",
        icon: AlertCircle,
        route: "/pages-all-leads",
        tone: "danger",
      },
    ],
    [data]
  );

  const userSegments = [
    { label: "Contractors", value: numberValue(data.users.contractor), color: "#1f57d6" },
    { label: "Homeowners", value: numberValue(data.users.homeowner), color: "#16a085" },
    { label: "Real estate agents", value: numberValue(data.users.real_estate_agent), color: "#7c3aed" },
    { label: "Subcontractors", value: numberValue(data.users.subcontractor), color: "#e96904" },
  ];

  const maxTrend = Math.max(
    1,
    ...data.projectTrend.map((item) => numberValue(item.total))
  );
  const milestoneCompletion = numberValue(data.milestones.total)
    ? Math.round(
        (numberValue(data.milestones.completed) / numberValue(data.milestones.total)) * 100
      )
    : 0;

  return (
    <div className="admin-command-center">
      <Tabs defaultActiveKey="overview" className="admin-dashboard-tabs mb-4">
        <Tab eventKey="overview" title="Project overview">
          <div className="admin-dashboard-hero mb-4">
            <div>
              <div className="admin-eyebrow">
                <Activity size={15} /> Platform command center
              </div>
              <h2>Good overview. Better decisions.</h2>
              <p>
                Projects, people, contracts, leads, delivery, and customer attention in one place.
              </p>
            </div>
            <div className="d-flex align-items-center gap-3">
              {data.generatedAt ? (
                <span className="small text-white-50 d-none d-md-inline">
                  Updated {new Date(data.generatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
              ) : null}
              <Button variant="light" onClick={loadDashboard} disabled={loading}>
                <RefreshCw size={16} className={loading ? "admin-spin me-2" : "me-2"} />
                Refresh
              </Button>
            </div>
          </div>

          {error ? (
            <Alert variant="danger" className="d-flex align-items-center justify-content-between">
              <span>{error}</span>
              <Button size="sm" variant="outline-danger" onClick={loadDashboard}>Try again</Button>
            </Alert>
          ) : null}

          {loading && !data.generatedAt ? (
            <Card>
              <Card.Body className="py-5 text-center">
                <Spinner animation="border" />
                <div className="text-muted mt-3">Building the platform overview…</div>
              </Card.Body>
            </Card>
          ) : (
            <>
              <Row className="g-3 mb-4">
                <Col xl={3} md={6}>
                  <MetricCard
                    label="Total project value"
                    value={formatCurrency(data.projects.total_value)}
                    note={`${numberValue(data.projects.live)} live of ${numberValue(data.projects.total)} projects`}
                    icon={WalletCards}
                    tone="tone-blue"
                  />
                </Col>
                <Col xl={3} md={6}>
                  <MetricCard
                    label="Live projects"
                    value={numberValue(data.projects.live)}
                    note={`${numberValue(data.projects.new_last_30_days)} created in the last 30 days`}
                    icon={Building2}
                    tone="tone-green"
                  />
                </Col>
                <Col xl={3} md={6}>
                  <MetricCard
                    label="Open leads"
                    value={numberValue(data.leads.open)}
                    note={`${numberValue(data.leads.accepted)} accepted · ${numberValue(data.leads.completed)} completed`}
                    icon={ClipboardList}
                    tone="tone-purple"
                  />
                </Col>
                <Col xl={3} md={6}>
                  <MetricCard
                    label="Platform users"
                    value={numberValue(data.users.total)}
                    note={`${numberValue(data.users.contractor)} contractors · ${numberValue(data.users.homeowner)} homeowners`}
                    icon={Users}
                    tone="tone-orange"
                  />
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col xl={8}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div>
                          <h5 className="mb-1">Project pipeline</h5>
                          <div className="text-muted small">New projects created over the last six months</div>
                        </div>
                        <Badge bg="primary-subtle" text="primary">
                          {numberValue(data.projects.total)} total
                        </Badge>
                      </div>
                      <div className="admin-trend-chart">
                        {data.projectTrend.length ? (
                          data.projectTrend.map((item) => {
                            const value = numberValue(item.total);
                            return (
                              <div className="admin-trend-column" key={item.month_key}>
                                <div className="admin-trend-value">{value}</div>
                                <div
                                  className="admin-trend-bar"
                                  style={{ height: `${Math.max(8, (value / maxTrend) * 150)}px` }}
                                />
                                <div className="admin-trend-label">{item.month}</div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-muted m-auto">No recent project activity.</div>
                        )}
                      </div>
                      <div className="admin-pipeline-summary">
                        <div>
                          <span className="pipeline-dot live" />
                          <strong>{numberValue(data.projects.live)}</strong> live
                        </div>
                        <div>
                          <span className="pipeline-dot draft" />
                          <strong>{numberValue(data.projects.draft)}</strong> draft
                        </div>
                        <div>
                          <span className="pipeline-dot signed" />
                          <strong>{numberValue(data.contracts.signed)}</strong> signed contracts
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <h5 className="mb-1">Needs attention</h5>
                      <div className="text-muted small mb-3">Highest-value operational follow-ups</div>
                      <div className="admin-attention-list">
                        {attentionItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link to={item.route} className="admin-attention-item" key={item.label}>
                              <span className={`attention-icon ${item.tone}`}>
                                <Icon size={18} />
                              </span>
                              <span className="flex-grow-1">
                                <span className="d-flex justify-content-between gap-2">
                                  <strong>{item.label}</strong>
                                  <b>{item.value}</b>
                                </span>
                                <small>{item.detail}</small>
                              </span>
                              <ArrowRight size={16} />
                            </Link>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col xl={8}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <h5 className="mb-1">Recent projects</h5>
                          <div className="text-muted small">Contract and delivery readiness across the newest work</div>
                        </div>
                        <Button as={Link as any} to="/get-contracts" variant="outline-primary" size="sm">
                          View contracts
                        </Button>
                      </div>
                      <div className="table-responsive">
                        <Table hover className="align-middle admin-project-table mb-0">
                          <thead>
                            <tr>
                              <th>Project</th>
                              <th>Team</th>
                              <th>Contract</th>
                              <th>Milestones</th>
                              <th className="text-end">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.recentProjects.length ? (
                              data.recentProjects.map((project) => {
                                const total = numberValue(project.total_milestones);
                                const complete = numberValue(project.completed_milestones);
                                const progress = total ? Math.round((complete / total) * 100) : 0;
                                return (
                                  <tr key={project.id}>
                                    <td>
                                      <div className="fw-semibold">{project.name || `Project #${project.id}`}</div>
                                      <div className="small text-muted">
                                        {[project.city, project.state].filter(Boolean).join(", ") || "Location not set"} · {formatDate(project.created_at)}
                                      </div>
                                      <Badge bg={statusVariant(project.status)} className="mt-1">
                                        {humanize(project.status)}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div>{project.homeowner_name?.trim() || "Homeowner not assigned"}</div>
                                      <div className="small text-muted">
                                        {project.contractor_company || project.contractor_name?.trim() || "Contractor not assigned"}
                                      </div>
                                    </td>
                                    <td>
                                      <Badge bg={statusVariant(project.contract_status)}>
                                        {humanize(project.contract_status)}
                                      </Badge>
                                    </td>
                                    <td style={{ minWidth: 135 }}>
                                      <div className="small mb-1">{complete} of {total} complete</div>
                                      <ProgressBar now={progress} variant={progress === 100 ? "success" : "primary"} />
                                    </td>
                                    <td className="text-end fw-semibold">{formatCurrency(project.total_cost)}</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr><td colSpan={5} className="py-4 text-center text-muted">No projects found.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex align-items-start justify-content-between mb-4">
                        <div>
                          <h5 className="mb-1">Delivery health</h5>
                          <div className="text-muted small">Milestone completion across all projects</div>
                        </div>
                        <ShieldCheck className="text-success" size={24} />
                      </div>
                      <div className="admin-health-score">
                        <span>{milestoneCompletion}%</span>
                        <small>complete</small>
                      </div>
                      <ProgressBar now={milestoneCompletion} variant="success" className="mb-4" />
                      <div className="admin-stat-grid">
                        <div><strong>{numberValue(data.milestones.completed)}</strong><span>Completed</span></div>
                        <div><strong>{numberValue(data.milestones.pending)}</strong><span>Pending</span></div>
                        <div><strong>{numberValue(data.estimates.new)}</strong><span>New estimates</span></div>
                        <div><strong>{numberValue(data.support.open)}</strong><span>Open tickets</span></div>
                      </div>
                      <Button as={Link as any} to="/free-estimates" variant="primary" className="w-100 mt-4">
                        Review incoming estimates
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3">
                <Col xl={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <h5 className="mb-1">Platform community</h5>
                      <div className="text-muted small mb-4">User mix across the RELiBUILD network</div>
                      <div className="vstack gap-3">
                        {userSegments.map((segment) => {
                          const percentage = numberValue(data.users.total)
                            ? Math.round((segment.value / numberValue(data.users.total)) * 100)
                            : 0;
                          return (
                            <div key={segment.label}>
                              <div className="d-flex justify-content-between mb-1">
                                <span>{segment.label}</span>
                                <strong>{segment.value}</strong>
                              </div>
                              <div className="admin-user-track">
                                <span style={{ width: `${percentage}%`, background: segment.color }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="d-flex gap-2 mt-4">
                        <Button as={Link as any} to="/pages-all-contractors" variant="outline-primary" className="flex-fill">Contractors</Button>
                        <Button as={Link as any} to="/pages-all-homeowners" variant="outline-primary" className="flex-fill">Homeowners</Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <h5 className="mb-1">Latest leads</h5>
                          <div className="text-muted small">Newest demand entering the platform</div>
                        </div>
                        <Button as={Link as any} to="/pages-all-leads" variant="link" size="sm">See all</Button>
                      </div>
                      <div className="admin-lead-list">
                        {data.recentLeads.length ? data.recentLeads.map((lead) => (
                          <div className="admin-lead-item" key={lead.id}>
                            <span className="lead-avatar">{(lead.homeowner_name?.trim()?.[0] || "H").toUpperCase()}</span>
                            <span className="flex-grow-1">
                              <span className="d-flex justify-content-between gap-2">
                                <strong>{lead.project_type || "New project"}</strong>
                                <Badge bg={statusVariant(lead.status)}>{humanize(lead.status)}</Badge>
                              </span>
                              <small>
                                {lead.homeowner_name?.trim() || "Homeowner"} · {[lead.city, lead.state].filter(Boolean).join(", ") || "Location pending"}
                              </small>
                            </span>
                            <b>{formatCurrency(lead.budget)}</b>
                          </div>
                        )) : <div className="text-muted text-center py-4">No leads found.</div>}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <h5 className="mb-1">Quick actions</h5>
                      <div className="text-muted small mb-3">Jump into the areas admins use most</div>
                      <div className="admin-quick-grid">
                        <Link to="/get-contracts"><FileSignature size={20} /><span>Contracts</span></Link>
                        <Link to="/pages-all-leads"><ClipboardList size={20} /><span>All leads</span></Link>
                        <Link to="/pages-all-contractors"><Users size={20} /><span>Contractors</span></Link>
                        <Link to="/free-estimates"><BriefcaseBusiness size={20} /><span>Estimates</span></Link>
                        <Link to="/apps-tickets-list"><Headphones size={20} /><span>Support</span></Link>
                        <Link to="/billing/payment-alerts"><WalletCards size={20} /><span>Payments</span></Link>
                      </div>
                      <div className="admin-system-note mt-4">
                        <CheckCircle2 size={20} />
                        <div>
                          <strong>Operations snapshot ready</strong>
                          <span>Billing monitoring remains available in its own dashboard tab.</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Tab>
        <Tab eventKey="billing" title="Billing operations">
          <AdminBillingDashboard />
        </Tab>
      </Tabs>
    </div>
  );
}
